/**
 * Round half away from zero.
 *
 * JavaScript's built-in `Math.round` uses "round half to positive infinity"
 * (e.g. `Math.round(-1.5)` returns `-1`, not `-2`). Lodash's `_.round` also
 * follows IEEE 754 "round half to even" (banker's rounding) in some edge
 * cases. Neither behaves the way most people expect for negative numbers.
 *
 * This function implements "round half away from zero" — the rounding rule
 * taught in school — so that `-1.5` rounds to `-2` and `1.5` rounds to `2`.
 * It does this by stripping the sign before rounding, then reapplying it.
 *
 * The result is returned as a fixed-decimal **string** (via `toFixed`) so it
 * can be displayed directly without further formatting.
 *
 * @param value    The number to round.
 * @param decimals Number of decimal places (default `0`).
 * @returns The rounded value as a fixed-decimal string.
 */
export function round(value: number, decimals: number = 0): string {
  const negate = value < 0;
  let abs = negate ? -value : value;

  const factor = Math.pow(10, decimals);
  abs = Math.round(abs * factor) / factor;

  if (negate) {
    abs = -abs;
  }

  return abs.toFixed(decimals);
}
